import {
  Circle,
  Defs,
  Document,
  Image,
  LinearGradient,
  Page,
  Path,
  Polygon,
  Rect,
  Stop,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  CERTIFICATE_BODY,
  CERTIFICATE_CERTIFIES,
  CERTIFICATE_COLORS as C,
  CERTIFICATE_EYEBROW,
  CERTIFICATE_PALETTE,
  CERTIFICATE_TITLE,
  CREDENTIAL_LABEL,
  curriculumVersionText,
  DATE_LABEL,
  ICertificateContent,
  IGradientStop,
  SEAL_GEOMETRY,
  SEAL_VIEW_BOX,
} from "training/components/certificateDesign";

export type { ICertificateContent } from "training/components/certificateDesign";

// US Letter, landscape, in points.
const PAGE_WIDTH = 792;
const PAGE_HEIGHT = 612;
const BAND = 16;
const SEAL_WIDTH = 84;
const SEAL_HEIGHT = (SEAL_WIDTH * SEAL_VIEW_BOX.height) / SEAL_VIEW_BOX.width;

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    backgroundColor: C.white,
  },
  band: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  sheet: {
    position: "absolute",
    top: BAND,
    left: BAND,
    right: BAND,
    bottom: BAND,
    padding: 8,
    backgroundColor: C.white,
  },
  inner: {
    flexGrow: 1,
    borderWidth: 1,
    borderColor: C.grayLine,
    paddingTop: 24,
    paddingBottom: 12,
    paddingHorizontal: 48,
    alignItems: "center",
    justifyContent: "center",
  },
  top: {
    alignItems: "center",
  },
  logo: {
    height: 36,
    marginBottom: 12,
  },
  logoText: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: C.navy,
    marginBottom: 12,
  },
  eyebrow: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: C.gray,
    letterSpacing: 2.6,
  },
  title: {
    fontSize: 40,
    fontFamily: "Helvetica-Bold",
    color: CERTIFICATE_PALETTE.title,
    marginTop: 4,
    marginBottom: 14,
  },
  certifies: {
    fontSize: 13,
    fontFamily: "Helvetica-Oblique",
    color: C.gray,
  },
  name: {
    fontSize: 40,
    fontFamily: "Times-Bold",
    color: C.navy,
    marginTop: 6,
    textAlign: "center",
  },
  rule: {
    marginTop: 8,
    marginBottom: 12,
  },
  hospital: {
    fontSize: 15,
    fontFamily: "Helvetica-Bold",
    color: C.navy,
    marginBottom: 6,
  },
  body: {
    fontSize: 13,
    color: C.gray,
    textAlign: "center",
    maxWidth: 440,
    lineHeight: 1.4,
  },
  footer: {
    width: "100%",
    marginTop: 34,
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  field: {
    width: 190,
    alignItems: "center",
  },
  fieldValue: {
    width: "100%",
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: C.grayLight,
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: C.navy,
    textAlign: "center",
  },
  fieldValueMono: {
    fontFamily: "Courier-Bold",
    letterSpacing: 1,
  },
  fieldLabel: {
    marginTop: 5,
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: C.gray,
    letterSpacing: 1.6,
  },
  version: {
    marginTop: 14,
    fontSize: 8,
    color: C.gray,
  },
});

function GradientStops({ stops }: { stops: IGradientStop[] }) {
  return (
    <>
      {stops.map(({ offset, color }) => (
        <Stop key={offset} offset={offset} stopColor={color} />
      ))}
    </>
  );
}

/** The seal, drawn from the same SEAL_GEOMETRY as the screen; or the supplied artwork when there is one. */
function PdfSeal({ imageSrc }: { imageSrc?: string | null }) {
  if (imageSrc) {
    return (
      <Image
        src={imageSrc}
        style={{ width: SEAL_WIDTH, height: SEAL_HEIGHT, objectFit: "contain" }}
      />
    );
  }
  const { seal } = CERTIFICATE_PALETTE;
  const g = SEAL_GEOMETRY;
  return (
    <Svg
      width={SEAL_WIDTH}
      height={SEAL_HEIGHT}
      viewBox={`0 0 ${SEAL_VIEW_BOX.width} ${SEAL_VIEW_BOX.height}`}
    >
      <Defs>
        <LinearGradient id="seal" x1="0" y1="0" x2="1" y2="1">
          <GradientStops stops={seal.rosette} />
        </LinearGradient>
      </Defs>
      <Polygon points={g.tails[0]} fill={seal.tails[0]} />
      <Polygon points={g.tails[1]} fill={seal.tails[1]} />
      <Polygon points={g.rosette} fill="url(#seal)" />
      <Circle
        cx={g.center.x}
        cy={g.center.y}
        r={g.ringRadius}
        fill={seal.ring}
      />
      <Circle
        cx={g.center.x}
        cy={g.center.y}
        r={g.discRadius}
        fill={seal.disc}
      />
      <Circle
        cx={g.center.x}
        cy={g.center.y}
        r={g.hairlineRadius}
        fill="none"
        stroke={seal.hairline}
        strokeWidth={1}
      />
      <Path
        d={g.check}
        fill="none"
        stroke={seal.check}
        strokeWidth={g.checkWidth}
        strokeLineCap="round"
      />
    </Svg>
  );
}

export interface ICertificatePdfProps extends ICertificateContent {
  /** The logo PNG's absolute URL (or a data URI); without one the PDF prints "Clear Health" in type. */
  logoSrc?: string | null;
  /** Supplied seal artwork's absolute URL (SEAL_IMAGE_SRC on the page's origin); without one the seal is drawn. */
  sealImageSrc?: string | null;
}

/**
 * CLE-3015 — the Clear Certified certificate as a PDF (Download PDF on the certificate page), the same
 * design as the screen (CertificateParts.tsx) from the same constants. Rendered in the browser with
 * @react-pdf/renderer, which has no CSS: the gradients are SVG, the fonts are the PDF standard ones
 * (Helvetica for the sans, Times for the name), and the logo is the app's PNG, fetched by URL.
 */
export function CertificatePdf({
  name,
  hospitalName,
  certifiedDate,
  curriculumVersion,
  credentialId,
  logoSrc,
  sealImageSrc,
}: ICertificatePdfProps) {
  return (
    <Document title="Clear Certified certificate" author="Clear Health">
      <Page size="LETTER" orientation="landscape" style={styles.page}>
        <Svg
          width={PAGE_WIDTH}
          height={PAGE_HEIGHT}
          viewBox={`0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}`}
          style={styles.band}
          fixed
        >
          <Defs>
            <LinearGradient id="band" x1="0" y1="0" x2="1" y2="1">
              <GradientStops stops={CERTIFICATE_PALETTE.band} />
            </LinearGradient>
          </Defs>
          <Rect
            x={0}
            y={0}
            width={PAGE_WIDTH}
            height={PAGE_HEIGHT}
            fill="url(#band)"
          />
        </Svg>
        <View style={styles.sheet}>
          <View style={styles.inner}>
            <View style={styles.top}>
              {logoSrc ? (
                <Image src={logoSrc} style={styles.logo} />
              ) : (
                <Text style={styles.logoText}>Clear Health</Text>
              )}
              <Text style={styles.eyebrow}>
                {CERTIFICATE_EYEBROW.toUpperCase()}
              </Text>
              <Text style={styles.title}>{CERTIFICATE_TITLE}</Text>
              <Text style={styles.certifies}>{CERTIFICATE_CERTIFIES}</Text>
              <Text style={styles.name}>{name}</Text>
              <Svg width={240} height={3} style={styles.rule}>
                <Defs>
                  <LinearGradient id="rule" x1="0" y1="0" x2="1" y2="0">
                    <GradientStops stops={CERTIFICATE_PALETTE.rule} />
                  </LinearGradient>
                </Defs>
                <Rect x={0} y={0} width={240} height={3} fill="url(#rule)" />
              </Svg>
              {hospitalName ? (
                <Text style={styles.hospital}>{hospitalName}</Text>
              ) : null}
              <Text style={styles.body}>{CERTIFICATE_BODY}</Text>
            </View>
            <View style={styles.footer}>
              <View style={styles.field}>
                <Text style={styles.fieldValue}>{certifiedDate}</Text>
                <Text style={styles.fieldLabel}>
                  {DATE_LABEL.toUpperCase()}
                </Text>
              </View>
              <PdfSeal imageSrc={sealImageSrc} />
              <View style={styles.field}>
                {credentialId ? (
                  <>
                    <Text style={[styles.fieldValue, styles.fieldValueMono]}>
                      {credentialId}
                    </Text>
                    <Text style={styles.fieldLabel}>
                      {CREDENTIAL_LABEL.toUpperCase()}
                    </Text>
                  </>
                ) : null}
              </View>
            </View>
            {curriculumVersion ? (
              <Text style={styles.version}>
                {curriculumVersionText(curriculumVersion)}
              </Text>
            ) : null}
          </View>
        </View>
      </Page>
    </Document>
  );
}
